import { useRef, useState, useCallback } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../button';
import { UploadFileIcon } from '../../../custom/upload-file-icon';

export function DropZone(props: {
	id?: string;
	accept?: string;
	multiple?: boolean;
	description?: string;
	label: string;
	hint?: string;
	onFiles: (files: File[]) => void;
	className?: string;
}) {
	const { onFiles } = props;
	const accept = props.accept ?? 'image/*';
	const multiple = props.multiple ?? false;
	const inputRef = useRef<HTMLInputElement>(null);
	const dragCounter = useRef(0);
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		dragCounter.current++;
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		dragCounter.current--;
		if (dragCounter.current === 0) setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			dragCounter.current = 0;
			setIsDragOver(false);
			const files = Array.from(e.dataTransfer.files);
			if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1));
		},
		[onFiles, multiple],
	);

	return (
		<div
			className={cn(
				'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-10 transition-[border-color,background-color] duration-200',
				isDragOver ? 'border-primary bg-primary/5' : 'border-input',
				props.className,
			)}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={(e) => e.preventDefault()}
			onDrop={handleDrop}
		>
			<div
				className={cn(
					'rounded-lg bg-[#e1e9e7] p-2 shadow-[0px_0px_0px_3px_rgba(229,238,236,0.4)] transition-transform duration-200',
					isDragOver && 'scale-110',
				)}
			>
				<UploadFileIcon className="size-4 text-[#5C7972]" />
			</div>
			<p className="text-muted-foreground mt-3 text-center text-sm">
				{props.description ?? 'Drag and drop files here, or upload from your computer'}
			</p>
			{props.hint && <p className="text-muted-foreground mt-1 text-center text-xs">{props.hint}</p>}
			<Button type="button" variant="outline-dark" size="lg" className="mt-3" onClick={() => inputRef.current?.click()}>
				{props.label}
			</Button>
			<input
				ref={inputRef}
				id={props.id}
				type="file"
				accept={accept}
				multiple={multiple}
				aria-label={props.label}
				className="hidden"
				onChange={(e) => {
					const files = e.target.files ? Array.from(e.target.files) : [];
					if (files.length > 0) props.onFiles(files);
					e.target.value = '';
				}}
			/>
		</div>
	);
}
